import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { useUpdateEffect } from 'react-use'
import { DialogContent, DialogHeader } from '@/components/ui/dialog'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DatabaseForm, type DatabaseFormRef } from './Form'
import { convertTableToFormilySchema, type IColumnType } from '../shared'
import { useDatabaseContext } from '../context'
import type { ProjectTable, TableColumnDefinition } from '@/lib/types'
import { cloneDeep, forEach, omit } from 'lodash'
import { DialogTitle } from '@radix-ui/react-dialog'
import { Spin } from '@/components/ui/spin'
import { useTranslations } from 'next-intl'
import { fetcher } from '@/lib/utils'

export interface DatabaseFormDialogProps {
  table: TableColumnDefinition
  record?: any
  mode: 'CREATE' | 'EDIT'
  children?: React.ReactNode
  onClose: () => void
}

export function DatabaseFormDialog({
  table,
  record,
  onClose,
  mode,
}: DatabaseFormDialogProps) {
  const {
    project,
    loading,
    selectedTableColumn,
    tableName,
    crud: { create, update },
  } = useDatabaseContext()

  const [formValue, setFormValue] = useState<any>(record) // 表单值
  const [tableDef, setTableDef] = useState(table)

  const t = useTranslations('database')
  const tglobal = useTranslations('global')

  const rawSchema = useMemo(
    () => convertTableToFormilySchema(tableDef, tglobal),
    [tableDef, tglobal],
  )

  const columnRequest = useCallback(
    async (body: any) => {
      const resp = await fetcher(
        `/api/me/project/${project?._id}/table/${tableName}/columns`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        },
      )

      return resp
    },
    [project, tableName],
  )

  const schema = useMemo(() => {
    const modifiedSchema = cloneDeep(rawSchema)
    // 对协议做一些前端映射
    forEach(modifiedSchema.properties, (field, key) => {
      // popover 属性适配
      if (
        ['DateTimePicker', 'RichText', 'SelectV2'].includes(
          field['x-component'],
        )
      ) {
        field['x-component-props'] = {
          ...(field['x-component-props'] || {}),
          modal: true,
        }
      }

      if (field['x-component'] === 'RichText') {
        field['x-component-props'] = {
          ...(field['x-component-props'] || {}),
          className: 'bg-accent',
          editorContentClassName: 'w-full flex flex-col flex-1',
          output: 'html',
          placeholder: t('richtext_placeholder'),
          autofocus: true,
          editable: true,
          editorClassName: 'focus:outline-hidden px-5 py-4 flex-1',
          immediatelyRender: false,
        }
      }

      // id 不可变更
      if (['id', 'uuid'].includes(key)) {
        field['x-component-props'] = {
          ...(field['x-component-props'] || {}),
          readOnly: true,
        }
      }
      // 生成时间不可变更，DatePicker 不支持 readOnly，这里用 disabled 替换
      if (['created_at', 'updated_at'].includes(key)) {
        field['x-component-props'] = {
          ...(field['x-component-props'] || {}),
          disabled: true,
        }
        if (mode === 'CREATE') {
          field['x-visible'] = false
        } else if (mode === 'EDIT') {
          // 编辑模式有时候 ai 不会返回创建时间和更新时间，此时不展示
          if (field['x-validator'][0].required === false) {
            field['x-visible'] = false
          }
        }
      }

      if (field['x-column-type'] === 'tag') {
        field['x-component-props'] = {
          ...(field['x-component-props'] || {}),
          showSearch: true,
          allowCreate: !field['x-column-props'].foreign_key?.field,
          onCreateOption: async (value: string) => {
            const { tag_values = [] } = field['x-column-props'] || {}
            const newTag = {
              [key]: { tag_values: [...tag_values, value] },
            }
            await columnRequest(newTag)
            setFormValue((prev: any) => {
              return {
                ...prev,
                [key]: value,
              }
            })
            setTableDef((prev) => ({
              ...prev,
              field_list: prev.field_list.map((d) => {
                if (d.db_name === key) {
                  return {
                    ...d,
                    // @ts-ignore
                    tag_values: [...d.tag_values, value],
                  }
                }
                return d
              }),
            }))
            return { label: value, value }
          },
          multiple: field['x-column-props']?.tag_type === 'multiple',
        }
      }
    })

    return modifiedSchema
  }, [rawSchema])

  const databaseFormRef = useRef<DatabaseFormRef | null>(null)

  const closeDialog = () => {
    onClose?.()
  }

  const createRecord = async (values: Record<string, any>) => {
    // supabase 无法生成时间戳，前端临时生成
    // const createTimestamp = new Date().toISOString()
    return create({
      ...values,
      // created_at: createTimestamp,
      // updated_at: createTimestamp,
    })
  }

  const editRecord = async (
    id: string | number,
    values: Record<string, any>,
  ) => {
    const editValues = {
      ...values,
      // updated_at: new Date().toISOString(),
    }
    return update(id, omit(editValues, ['created_at', 'id']))
  }

  const handleSave = () => {
    databaseFormRef.current?.submit().then((values) => {
      if (!values) return
      const action =
        mode === 'EDIT' && record.id
          ? editRecord(record.id, omit(values, ['id']))
          : createRecord(values)
      action.then((res) => {
        setTimeout(() => closeDialog(), 300)
      })
    })
  }

  useUpdateEffect(() => {
    setFormValue(record)
  }, [record])

  return (
    <DialogContent
      className="container mx-auto h-full flex flex-col bg-[#0C1522] bg-modal-gradient rounded-lg border-modal-border-color"
      aria-describedby={undefined}
      noCloseButton
    >
      <DialogHeader>
        <DialogTitle className="hidden">{t('database')}</DialogTitle>
      </DialogHeader>
      <div className="flex justify-between items-center h-auto mb-2">
        <div className="flex items-center gap-2 cursor-pointer">
          <ArrowLeft onClick={closeDialog} />
          <div className="text-xl">
            {mode === 'EDIT' ? t('edit') : t('add_new')}
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            type="button"
            size="custom"
            variant="ghost"
            onClick={closeDialog}
            className="rounded-lg px-8 py-2 hover:decoration-transparent"
          >
            {t('cancel')}
          </Button>
          <Button
            type="button"
            size="custom"
            variant="ghost"
            className="rounded-lg px-8 py-2 bg-white text-[#222] hover:bg-white hover:text-[#222]"
            loading={loading.crud.CREATE || loading.crud.UPDATE}
            onClick={handleSave}
          >
            {t('save')}
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <Spin spinning={loading.crud.CREATE}>
          <DatabaseForm
            schema={schema}
            ref={databaseFormRef}
            values={formValue}
          />
        </Spin>
      </div>
    </DialogContent>
  )
}
